import { useEffect, useRef, useState } from "react";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./GenderDropdown.module.css";

interface GenderDropdownProps {
	value: string;
	onChange: (value: string) => void;
	className?: string;
}

export function GenderDropdown({
	value,
	onChange,
	className,
}: GenderDropdownProps) {
	const { t } = useClientTranslations("onboarding");
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const options = [
		{
			value: t("questions.genderRelationship.genderOptions.male"),
			label: t("questions.genderRelationship.genderOptions.male"),
		},
		{
			value: t("questions.genderRelationship.genderOptions.female"),
			label: t("questions.genderRelationship.genderOptions.female"),
		},
		{
			value: t("questions.genderRelationship.genderOptions.nonBinary"),
			label: t("questions.genderRelationship.genderOptions.nonBinary"),
		},
		{
			value: t("questions.genderRelationship.genderOptions.preferNotToSay"),
			label: t("questions.genderRelationship.genderOptions.preferNotToSay"),
		},
	];

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}
	}, [isOpen]);

	const handleSelect = (selectedValue: string) => {
		onChange(selectedValue);
		setIsOpen(false);
	};

	return (
		<div className={`${styles.dropdown} ${className || ""}`} ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ""}`}
			>
				<span className={value ? styles.selectedText : styles.placeholder}>
					{value || t("questions.genderRelationship.selectGender")}
				</span>
				<svg
					className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
					width="14"
					height="8"
					viewBox="0 0 14 8"
					fill="none"
					aria-hidden="true"
				>
					<path
						d="M1 1L7 7L13 1"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>

			{isOpen && (
				<div className={styles.menu}>
					{options.map((option) => (
						<button
							key={option.value}
							type="button"
							onClick={() => handleSelect(option.value)}
							className={`${styles.option} ${value === option.value ? styles.optionSelected : ""}`}
						>
							{option.label}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
